import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { getDisplayImageUrl } from '../utils/imageUtils';

export const ProductContext = createContext();

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // ms

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageCache, setImageCache] = useState({}); // Caché para las imágenes
  const [error, setError] = useState(null);

  // Evita fetches duplicados/concurrentes (p. ej. doble invocación del efecto
  // de montaje en React.StrictMode, o un refetch manual mientras otro sigue en curso).
  const isFetchingRef = useRef(false);

  const fetchProducts = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    setLoading(true);
    setError(null);

    let lastError = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/products?limit=10000&page=1`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        const productList = data.products || data;

        if (!Array.isArray(productList)) {
          throw new Error('Respuesta inválida del servidor');
        }

        console.log(`📦 Productos cargados: ${productList.length}${data.pagination ? ` de ${data.pagination.total} totales` : ''}`);
        setProducts(productList);

        // Generar caché de imágenes (URLs del proxy con cache CDN)
        const newImageCache = {};
        productList.forEach(product => {
          if (product.image && !newImageCache[product.image]) {
            newImageCache[product.image] = getDisplayImageUrl(product.image);
          }
        });

        setImageCache(prevCache => ({ ...prevCache, ...newImageCache }));

        lastError = null;
        break;
      } catch (err) {
        lastError = err;
        console.error(`Error al cargar productos (intento ${attempt + 1}/${MAX_RETRIES}):`, err);

        if (attempt < MAX_RETRIES - 1) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (attempt + 1)));
        }
      }
    }

    if (lastError) {
      setError(lastError.message);
    }

    setLoading(false);
    isFetchingRef.current = false;
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return (
    <ProductContext.Provider value={{ products, setProducts, imageCache, loading, error, refetchProducts: fetchProducts }}>
      {children}
    </ProductContext.Provider>
  );
};